import React, { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import { BuySourceCard } from "./BuySourceCard";
import { normalizeBuySources } from "./normalizeSource";
import { SourcePreviewOverlay } from "./SourcePreviewOverlay";
import { useSourcePreview } from "./useSourcePreview";

/**
 * Horizontal Buy Sources rail:
 * flex row, overflow-x auto, no wrap, all usable cards, no "+N sources" tile.
 * Native overflow scroll only — no pointer-capture drag (that blocked card <a> clicks).
 */
export function BuySourcesRail({
  sources = [],
  className,
  emptyLabel = "No buy sources linked yet.",
  testId = "buy-sources-rail",
  walmartTargetPrice,
  showEconomics = false,
  hideVerificationBadge = false,
}) {
  const ref = useRef(null);
  const normalized = normalizeBuySources(sources);
  const { preview, openPreview, close } = useSourcePreview();

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      if (el.scrollWidth <= el.clientWidth) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [normalized.length]);

  if (!normalized.length) {
    return <p className="text-[11px] text-ink-muted m-0 py-1">{emptyLabel}</p>;
  }

  return (
    <>
      <div
        ref={ref}
        className={cn("sfh-source-slider", className)}
        data-testid={testId}
        data-overflow-x="auto"
      >
        {normalized.map((s) => (
          <BuySourceCard
            key={s.id}
            source={s}
            onPreview={openPreview}
            walmartTargetPrice={walmartTargetPrice}
            showEconomics={showEconomics}
            hideVerificationBadge={hideVerificationBadge}
          />
        ))}
      </div>
      <SourcePreviewOverlay
        open={preview.open}
        loading={preview.loading}
        error={preview.error}
        data={preview.data}
        source={preview.source}
        onClose={close}
      />
    </>
  );
}
